import {
  formatDate,
} from './date-formats';

import {
  TGetQualityReportListResponse,
} from '@/api/quality';
import {
  TFindAuditResultsRouteResponse, TGetAuditSpecificationsResponse,
} from '@/generated/qmulus/axios/audit';

const MAX_DECIMAL_NOTATION_VALUE = 10 ** 6;

export function getFormattedValueForAuditTable(header: keyof TFindAuditResultsRouteResponse['items'][number], value: any) {
  switch (header) {
    case 'auditedAtUtc': return formatDate(value, true);
    default: return value;
  }
}

export function getFormattedValueForQualityTable(header: keyof TGetQualityReportListResponse['items'][number], value: any) {
  switch (header) {
    case 'orderCreatedAtUtc': return formatDate(value, true);
    case 'orderNumbers': return value.join(', ');
    default: return value;
  }
}

export function getFormattedValueForAuditSpecificationTable(header: keyof TGetAuditSpecificationsResponse['items'][number], value: any) {
  switch (header) {
    case 'envelopes': return value.length;
    default: return value;
  }
}

/**
 * Formats a given number to a specified precision, removing any trailing zeros.
 * Switches to exponential notation for very small values (smaller than 10^-precision).
 *
 * @param {number} value - The number to be formatted.
 * @param {number} [precision=2] - The number of decimal places to keep (default is 2).
 * @returns {string} The formatted number as a string.
 *
 * @example
 * formatNumber(123.456789); // Returns "123.46"
 * formatNumber(123.40000);  // Returns "123.4"
 * formatNumber(123, 0);     // Returns "123"
 * formatNumber(2.4, 5);     // Returns "2.4"
 * formatNumber(2.401, 5);   // Returns "2.401"
 * formatNumber(0, 5);       // Returns "0"
 * formatNumber(0.00123, 2);   // Returns "1.23e-3" (exponential for values < 10^-2)
 * formatNumber(12345678901, 2); // Returns "1.23e+10" (exponential for values with 10+ characters)
 */
export function formatNumber(value: number | null | undefined, precision: number = 2): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (value === 0) {
    return '0';
  }

  if (Math.abs(value) < 10 ** -precision || Math.abs(value) > MAX_DECIMAL_NOTATION_VALUE) {
    return value.toExponential(precision);
  }

  return parseFloat(value.toFixed(precision)).toString();
}
